// The tutor conversation, kept alive across close/reopen.
//
// TutorPanel held its turns in component state, and closing the panel unmounts
// it — so a student who read an explanation, closed the panel to look at the
// slide, and opened it again found an empty tutor and pressed Explain a second
// time. On a cached variant that costs nothing but the confusion; on a typed
// follow-up it is a real, billed regeneration of an answer they already had.
// Instructor: "ensure data persists so when user clicks close and open data is
// there".
//
// KEYED PER SLIDE, not per lesson. The tutor is grounded in one slide, so
// carrying slide 9's conversation onto slide 10 would attach an answer to a
// question about something else. Moving between slides shows that slide's own
// history, and coming back shows it again.
//
// In memory only, deliberately. It survives closing the panel and moving
// around the lesson, which is what was asked for; it does not survive a reload,
// and it never reaches a server. Nothing here is student data worth persisting
// — the answers are reproducible and the cacheable ones are already cached.

// Generic in the turn type: the store's job is to keep an array alive across
// an unmount, and it has no business knowing what a turn looks like. TutorPanel
// owns that shape and gets it back unchanged, so neither side needs a cast
// through `unknown` and neither imports a type from the other.
const conversations = new Map<string, unknown[]>();

const keyFor = (lessonId: string, slideIndex: number) => `${lessonId}:${slideIndex}`;

export function loadConversation<T>(lessonId: string, slideIndex: number): T[] {
  return (conversations.get(keyFor(lessonId, slideIndex)) ?? []) as T[];
}

export function saveConversation<T>(lessonId: string, slideIndex: number, turns: T[]): void {
  const key = keyFor(lessonId, slideIndex);
  // An empty conversation is an absence, not a value: storing [] would keep a
  // key per slide visited for the life of the tab and say nothing.
  if (turns.length === 0) conversations.delete(key);
  else conversations.set(key, turns);
}

/** Used by the panel's own Clear button, and by tests between cases. */
export function clearConversation(lessonId: string, slideIndex: number): void {
  conversations.delete(keyFor(lessonId, slideIndex));
}

export function _resetAllConversations(): void {
  conversations.clear();
}
